import { plural, t, Trans } from "@lingui/macro";
import { useConfigContext, useSubscriptionContext } from "@WahooFitness/wahoo-offline-mfe";
import { useCallback, useMemo } from "react";
import { add, isValid } from "date-fns";
import { Alert, Button, Chip, Stack, Typography } from "@mui/material";
import AppStoreLogoSvg from "./AppStoreLogoSvg";
import GooglePlayLogoSvg from "./GooglePlayLogoSvg";
import { useLingui } from "@lingui/react";
import useRevenueCat from "./useRevenueCat";
import formatDate from "@/services/formatDate";
import useAuthTokenClient from "./useAuthTokenClient";
import { publish } from "@ionic/portals";
import { Tag } from "@carbon/icons-react";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import UISubscriptionStatusEnum from "./UISubscriptionStatusEnum";
import getUISubscriptionState from "./getUISubscriptionState";
import { getPendingSubscriptionChange } from "./getPendingSubscriptionChange";
import { SubscriptionType } from "@WahooFitness/cloud-client-types";
import { formatPromoPeriod, getInterval } from "./displayUtils";

export type NonSubscribedDataType = {
  status: string;
  aboveButtonText: string;
  belowButtonText?: string;
  buttonText: string;
  buttonDisabled: boolean;
  buttonAction: (offering: any) => void;
  linkHref?: string;
} | null;

const activeSubscriptionStates = [
  UISubscriptionStatusEnum.ActiveSubscription,
  UISubscriptionStatusEnum.ActiveCancelledSubscription,
  UISubscriptionStatusEnum.OnTrialNoSubscription,
  UISubscriptionStatusEnum.OnTrialFutureSubscription,
  UISubscriptionStatusEnum.PaymentError,
  UISubscriptionStatusEnum.HonorarySubscription,
  UISubscriptionStatusEnum.HonoraryRecurringSubscription,
  UISubscriptionStatusEnum.PriceCoupon,
  UISubscriptionStatusEnum.ImmediateIAPSubscription,
  UISubscriptionStatusEnum.OnIapTrial,
  UISubscriptionStatusEnum.ActiveTimeOffPromotion,
  UISubscriptionStatusEnum.TrialingTimeOffPromotion,
];

// Discount coupons are always single-interval.
function couponDurationLabel(interval: string | undefined): string {
  switch (interval) {
    case "daily":
      return t`day`;
    case "weekly":
      return t`week`;
    case "monthly":
      return t`month`;
    case "yearly":
      return t`year`;
    default:
      return interval ?? "";
  }
}

function useMembership(referrer?: string) {
  const { userSubscription, subscriptionError, subscriptionIsLoading } = useSubscriptionContext();
  const {
    fitnessAppId,
    cloudConfig: { wahooMyAccountUrl },
  } = useConfigContext();
  const { i18n } = useLingui();
  const { getAuthTokens } = useAuthTokenClient();
  const { logEvent } = useLogEvent();

  const {
    pkg,
    offeringsLoading,
    offeringsError,
    makePurchase,
    purchaseInProgress,
    getPeriodStringFromUnit,
    convertPeriodToDays,
  } = useRevenueCat();

  const logSubscriptionEvent = useCallback(
    (eventName: string, clickTarget: ClickTarget) => {
      logEvent(eventName, {
        event_type: EventType.click,
        click_target: clickTarget,
        source_page_name: SourcePage.my_subscription,
        referrer,
      });
    },
    [logEvent, referrer]
  );
  const userSubscriptionState = useMemo(
    () => getUISubscriptionState(userSubscription as SubscriptionType),
    [userSubscription]
  );

  const hasActiveSub = useMemo(
    () => activeSubscriptionStates.includes(userSubscriptionState),
    [userSubscriptionState]
  );

  const nonSubscribedData: NonSubscribedDataType = useMemo(() => {
    if (offeringsError) {
      return {
        status: "trialAvailable",
        aboveButtonText: t`Unable to reach the store. Please try again later.`,
        buttonText: t`Try again later.`,
        buttonDisabled: true,
        buttonAction: () => {},
      };
    }
    const trialDuration = convertPeriodToDays(pkg?.product.trialLength);
    const pricePerPeriod = pkg?.product.priceString ?? 0;
    const interval = getPeriodStringFromUnit(pkg?.product.period.unit);

    if (pkg?.product.trialAvailable) {
      return {
        status: "trialAvailable",
        aboveButtonText: t`Try it free for ${trialDuration} days, then ${pricePerPeriod}/${interval}.`,
        belowButtonText: t`Plans renew automatically. Cancel anytime from the "My subscription" page.`,
        buttonText: purchaseInProgress ? t`processing...` : t`Start free trial now`,
        buttonDisabled: purchaseInProgress,
        buttonAction: () => {
          logSubscriptionEvent("start_subscription", ClickTarget.start_trial);
          makePurchase(pkg?.identifier || "");
        },
      };
    } else {
      return {
        status: "trialExpired",
        aboveButtonText: t`Only ${pricePerPeriod}/${interval}.`,
        belowButtonText: t`Plans renew automatically. Cancel anytime from the "My subscription" page.`,
        buttonText: purchaseInProgress ? t`processing...` : t`Activate subscription now`,
        buttonDisabled: purchaseInProgress,
        buttonAction: () => {
          logSubscriptionEvent("start_subscription", ClickTarget.activate_subscription);
          makePurchase(pkg?.identifier || "");
        },
      };
    }
  }, [
    offeringsError,
    convertPeriodToDays,
    pkg?.product.trialLength,
    pkg?.product.priceString,
    pkg?.product.period.unit,
    pkg?.product.trialAvailable,
    pkg?.identifier,
    getPeriodStringFromUnit,
    purchaseInProgress,
    logSubscriptionEvent,
    makePurchase,
  ]);

  const handleManageCloudSubscription = useCallback(async () => {
    const response = await getAuthTokens();
    const { token } = response;
    const url = `${wahooMyAccountUrl}?faid=${fitnessAppId}&auth_token=${token}`;
    await publish({ topic: "openLinkExternally", data: { url } });
  }, [getAuthTokens, wahooMyAccountUrl, fitnessAppId]);

  const handleButtonClick = useCallback(
    async (buttonName: ClickTarget) => {
      logSubscriptionEvent("manage_or_cancel_subscription", buttonName);
      if (
        userSubscription?.subscription_processor?.name === "apple" ||
        userSubscription?.subscription_processor?.name === "google"
      ) {
        publish({
          topic: "openLinkExternally",
          data: { url: userSubscription.subscription_processor.url },
        });
      } else {
        handleManageCloudSubscription();
      }
    },
    [
      logSubscriptionEvent,
      userSubscription?.subscription_processor?.name,
      userSubscription?.subscription_processor?.url,
      handleManageCloudSubscription,
    ]
  );

  const subscriptionData = useMemo(() => {
    if (!userSubscription) {
      return null;
    }
    // convert subscription duration to date-fns format
    const fnsDuration = {
      daily: "days",
      weekly: "weeks",
      monthly: "months",
      yearly: "years",
    };

    const calculatePromoPeriodEndDate = (
      interval?: keyof typeof fnsDuration,
      intervalCount?: number,
      startDate?: string
    ) => {
      if (!interval || !intervalCount || !startDate) {
        return null;
      }
      const promoEnd = add(new Date(startDate), { [fnsDuration[interval]]: intervalCount });
      // isValid guards a malformed startDate so toISOString never throws.
      return isValid(promoEnd) ? promoEnd.toISOString() : null;
    };

    const {
      cancel_at,
      created_at,
      current_period_end,
      current_period_start,
      currency,
      interval,
      interval_count,
      status,
      subscription_pending_change,
      subscription_processor,
      subscription_amount_cents,
      subscription_coupon_redemption,
      trial_end,
      trial_start,
      next_renewal_at,
    } = userSubscription;

    const formatInterval = (interval: string | undefined) => {
      switch (interval) {
        case "monthly":
          return t`/month`;

        case "yearly":
          return t`/year`;
        default:
          return "";
      }
    };

    const formattedAmount =
      subscription_amount_cents && currency
        ? `${i18n.number(subscription_amount_cents / 100, {
            style: "currency",
            currency,
          })}${formatInterval(interval)}`
        : `$0`;

    const getDiscountTagLabel = (noCoupon = false) => {
      // this is for discounted time off subscriptions
      // we don't get the coupon data from the cloud
      if (noCoupon) {
        const { intervalCount: formattedIntervalCount, interval: formattedInterval } = getInterval(
          interval_count,
          interval,
          current_period_start,
          current_period_end
        );

        const durationPluralized = plural(formattedIntervalCount, {
          one: `First # ${formattedInterval} free`,
          other: `First # ${formattedInterval}s free`,
        });
        return t`${durationPluralized}`;
      }

      const {
        interval: couponInterval,
        interval_count: couponIntervalCount,
        pct_off,
      } = subscription_coupon_redemption?.subscription_coupon_promotion ?? {};

      if (!couponInterval || !couponIntervalCount) {
        return null;
      }
      const duration = couponDurationLabel(couponInterval);
      const convertedDurationText = couponInterval ? duration : null;
      if (pct_off !== null) {
        return t`${pct_off}% off first ${convertedDurationText}`;
      }
      return t`First ${convertedDurationText} free`;
    };

    switch (userSubscriptionState) {
      // immediate iap subscriber - we don't have dates/prices yet as the cloud webhook hasn't completed
      case UISubscriptionStatusEnum.ImmediateIAPSubscription:
        return {
          tag: status === "trialing" && (
            <Chip
              label={t`Free trial`}
              size="small"
              sx={{
                borderRadius: 0.5,
                maxWidth: "fit-content",
              }}
              color="default"
            />
          ),
          items: [
            {
              label: t`Member since`,
              value: formatDate({ date: created_at, format: "PP" }),
            },
          ],
          action: (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.cancel_subscription)}
                variant="text"
                fullWidth
                color="error"
              >
                {t`Cancel subscription`}
              </Button>
            </Stack>
          ),
          description: null,
        };
      case UISubscriptionStatusEnum.PriceCoupon: {
        const endDate = subscription_coupon_redemption
          ? calculatePromoPeriodEndDate(
              subscription_coupon_redemption.subscription_coupon_promotion
                ?.interval as keyof typeof fnsDuration,
              subscription_coupon_redemption.subscription_coupon_promotion?.interval_count,
              subscription_coupon_redemption.redeemed_at
            )
          : null;
        const subscriptionAmount = subscription_pending_change?.total_amount_cents
          ? subscription_pending_change.total_amount_cents / 100
          : 0;
        const chipLabel = status === "trialing" ? t`Trial` : getDiscountTagLabel();
        const chipColor = status === "trialing" ? "default" : "info";
        const chipIcon = status === "trialing" ? undefined : <Tag />;
        const items =
          status === "trialing"
            ? [
                {
                  label: t`Trial started`,
                  value: formatDate({ date: trial_start, format: "PP" }),
                },
                {
                  label: t`Trial ends`,
                  value: formatDate({ date: trial_end, format: "PP" }),
                },
                {
                  label: t`Promo period`,
                  value: formatPromoPeriod(subscription_coupon_redemption?.redeemed_at, endDate),
                },
              ]
            : [
                {
                  label: t`Member since`,
                  value: formatDate({ date: created_at, format: "PP" }),
                },
                {
                  label: t`Current Price`,
                  value: (
                    <Typography variant="prose-base" color="text.primary">
                      <s>{t`${i18n.number(subscriptionAmount, {
                        style: "currency",
                        currency: subscription_pending_change?.currency ?? "USD",
                      })}`}</s>{" "}
                      {t`${formattedAmount}`}
                    </Typography>
                  ),
                },
                {
                  label: t`Full price starts`,
                  value: formatDate({
                    date: userSubscription?.subscription_pending_change?.current_period_start,
                    format: "PP",
                  }),
                },
                {
                  label: t`Renews on`,
                  value: formatDate({
                    date: userSubscription?.next_renewal_at,
                    format: "PP",
                  }),
                },
              ];

        return {
          tag: (
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                borderRadius: 0.5,
                maxWidth: "fit-content",
              }}
              color={chipColor}
              icon={chipIcon}
            />
          ),
          items,
          action: (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.cancel_subscription)}
                variant="text"
                fullWidth
                color="error"
              >
                {t`Cancel subscription`}
              </Button>
            </Stack>
          ),
          description: status === "trialing" && (
            <Typography variant="prose-base" color="text.primary">
              <Trans>
                After the trial and promo periods end your subscription will be cancelled. To keep
                access to all features go to manage subscription.
              </Trans>
            </Typography>
          ),
        };
      }
      case UISubscriptionStatusEnum.HonorarySubscription: {
        const subscriptionAmount = subscription_pending_change?.total_amount_cents
          ? subscription_pending_change.total_amount_cents / 100
          : 0;
        const chipLabel = status === "trialing" ? t`Trial` : t`Complimentary`;
        const chipColor = status === "trialing" ? "default" : "info";
        const items =
          status === "trialing"
            ? [
                {
                  label: t`Trial started`,
                  value: formatDate({ date: trial_start, format: "PP" }),
                },
                {
                  label: t`Billing`,
                  value: (
                    <Typography variant="prose-base" color="text.primary">
                      <s>{t`${i18n.number(subscriptionAmount, {
                        style: "currency",
                        currency: subscription_pending_change?.currency ?? "USD",
                      })}`}</s>{" "}
                      {t`${formattedAmount}`}
                    </Typography>
                  ),
                },
                {
                  label: t`Billing starts`,
                  value: formatDate({
                    date: subscription_pending_change?.current_period_start,
                    format: "PP",
                  }),
                },
              ]
            : [
                {
                  label: t`Member since`,
                  value: formatDate({ date: created_at, format: "PP" }),
                },
                {
                  label: t`Promo period`,
                  value: formatPromoPeriod(current_period_start, current_period_end),
                },
              ];

        return {
          tag: (
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                borderRadius: 0.5,
                maxWidth: "fit-content",
              }}
              color={chipColor}
            />
          ),
          items,
          action: status === "trialing" && (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.cancel_subscription)}
                variant="text"
                fullWidth
                color="error"
              >
                {t`Cancel subscription`}
              </Button>
            </Stack>
          ),
          description: (
            <Typography variant="prose-base" color="text.primary">
              <Trans>
                After the promo period ends your subscription will be cancelled. To keep access to
                all features, go to manage subscription.
              </Trans>
            </Typography>
          ),
        };
      }
      case UISubscriptionStatusEnum.OnTrialNoSubscription:
        return {
          tag: (
            <Chip
              label={t`Free trial`}
              size="small"
              sx={{
                borderRadius: 0.5,
                maxWidth: "fit-content",
              }}
              color="default"
            />
          ),
          items: [
            {
              label: t`Trial started`,
              value: formatDate({ date: trial_start, format: "PP" }),
            },
            {
              label: t`Trial ends`,
              value: formatDate({ date: trial_end, format: "PP" }),
            },
          ],
          description: (
            <Typography variant="prose-base" color="text.primary">
              <Trans>
                After the trial period ends your subscription will be cancelled. To keep access to
                all features, go to manage subscription.
              </Trans>
            </Typography>
          ),
          action: (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
            </Stack>
          ),
        };
      case UISubscriptionStatusEnum.PaymentError: {
        return {
          items: [
            {
              label: t`Member since`,
              value: formatDate({ date: created_at, format: "PP" }),
            },
            {
              label: t`Billing`,
              value: t`${formattedAmount}`,
            },
            {
              label: t`Renews on`,
              value: formatDate({ date: next_renewal_at, format: "PP" }),
            },
          ],
          description: (
            <Alert severity="warning" variant="outlined">
              <Stack>
                <Typography variant="prose-base-medium">
                  <Trans>There was an issue processing your payment.</Trans>
                </Typography>
                <Typography variant="prose-sm">
                  <Trans>Please click the Manage subscription button for details.</Trans>
                </Typography>
              </Stack>
            </Alert>
          ),
          action: (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.cancel_subscription)}
                variant="text"
                fullWidth
                color="error"
              >
                {t`Cancel subscription`}
              </Button>
            </Stack>
          ),
          tag: null,
        };
      }
      case UISubscriptionStatusEnum.ActiveCancelledSubscription: {
        return {
          items: [
            {
              label: t`Member since`,
              value: formatDate({ date: created_at, format: "PP" }),
            },
            {
              label: t`Membership ends`,
              value: formatDate({ date: cancel_at, format: "PP" }),
            },
          ],
          description: (
            <Stack gap={2.5}>
              <Typography variant="prose-base" color="text.primary">
                <Trans>
                  You canceled your subscription with Wahoo. You will be able to use the Wahoo app
                  until the end of your current billing period.
                </Trans>
              </Typography>
              <Typography
                variant="prose-base-medium"
                color="text.primary"
                textAlign="center"
                mb={0.5}
              >
                {t`Keep access to all features for ${formattedAmount}.`}
              </Typography>
            </Stack>
          ),
          action: (
            <Button
              size="large"
              onClick={() => handleButtonClick(ClickTarget.resubscribe)}
              variant="contained"
              fullWidth
              color="primary"
            >
              {t`Resubscribe`}
            </Button>
          ),
          tag: null,
        };
      }
      case UISubscriptionStatusEnum.OnTrialFutureSubscription: {
        const pendingChange = getPendingSubscriptionChange(userSubscription);
        const amount = pendingChange?.subscription_amount_cents ?? 0;
        return {
          tag: (
            <Chip
              label={t`Free trial`}
              size="small"
              sx={{
                borderRadius: 0.5,
                maxWidth: "fit-content",
              }}
              color="default"
            />
          ),
          items: [
            {
              label: t`Trial started`,
              value: formatDate({ date: trial_start, format: "PP" }),
            },
            {
              label: t`Billing`,
              value: t`${i18n.number(amount / 100, {
                style: "currency",
                currency: pendingChange?.currency ?? "USD",
              })}${formatInterval(pendingChange?.interval)}`,
            },
            {
              label: t`Billing starts`,
              value: formatDate({
                date: pendingChange?.current_period_start,
                format: "PP",
              }),
            },
          ],
          description: null,
          action: (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.cancel_subscription)}
                variant="text"
                fullWidth
                color="error"
              >
                {t`Cancel subscription`}
              </Button>
            </Stack>
          ),
        };
        break;
      }
      case UISubscriptionStatusEnum.OnIapTrial: {
        const hasAppStoreMismatchWithDevice =
          (Number(fitnessAppId) === 30 && subscription_processor?.name === "google") ||
          (Number(fitnessAppId) === 31 && subscription_processor?.name === "apple");
        return {
          tag: (
            <Chip
              label={t`Free trial`}
              size="small"
              sx={{
                borderRadius: 0.5,
                maxWidth: "fit-content",
              }}
              color="default"
            />
          ),
          items: [
            {
              label: t`Trial started`,
              value: formatDate({ date: trial_start, format: "PP" }),
            },
            {
              label: t`Billing starts`,
              value: formatDate({
                date: next_renewal_at,
                format: "PP",
              }),
            },
          ],
          description: hasAppStoreMismatchWithDevice ? (
            <Stack alignItems="center">
              <Typography variant="prose-base-medium" color="text.primary">
                <Trans>To manage subscription go to</Trans>
              </Typography>
              {subscription_processor?.name === "apple" ? (
                <AppStoreLogoSvg />
              ) : (
                <GooglePlayLogoSvg />
              )}
            </Stack>
          ) : null,
          action: hasAppStoreMismatchWithDevice ? null : (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.cancel_subscription)}
                variant="text"
                fullWidth
                color="error"
              >
                {t`Cancel subscription`}
              </Button>
            </Stack>
          ),
        };
        break;
      }
      case UISubscriptionStatusEnum.ActiveSubscription: {
        const hasAppStoreMismatchWithDevice =
          (Number(fitnessAppId) === 30 && subscription_processor?.name === "google") ||
          (Number(fitnessAppId) === 31 && subscription_processor?.name === "apple");
        return {
          tag: null,
          items: [
            {
              label: t`Member since`,
              value: formatDate({ date: created_at, format: "PP" }),
            },
            {
              label: t`Billing`,
              value: t`${formattedAmount}`,
            },
            {
              label: t`Renews on`,
              value: formatDate({ date: next_renewal_at, format: "PP" }),
            },
          ],
          description: hasAppStoreMismatchWithDevice ? (
            <Stack alignItems="center">
              <Typography variant="prose-base-medium" color="text.primary">
                <Trans>To manage subscription go to</Trans>
              </Typography>
              {subscription_processor?.name === "apple" ? (
                <AppStoreLogoSvg />
              ) : (
                <GooglePlayLogoSvg />
              )}
            </Stack>
          ) : null,
          action: hasAppStoreMismatchWithDevice ? null : (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.cancel_subscription)}
                variant="text"
                fullWidth
                color="error"
              >
                {t`Cancel subscription`}
              </Button>
            </Stack>
          ),
        };
      }
      case UISubscriptionStatusEnum.HonoraryRecurringSubscription: {
        return {
          items: [
            {
              label: t`Member since`,
              value: formatDate({ date: created_at, format: "PP" }),
            },
            {
              label: t`Renews on`,
              value: formatDate({ date: next_renewal_at, format: "PP" }),
            },
          ],
          action: null,
          description: null,
          tag: (
            <Chip
              label={t`Complimentary`}
              size="small"
              sx={{
                borderRadius: 0.5,
                maxWidth: "fit-content",
              }}
              color="info"
            />
          ),
        };
      }
      case UISubscriptionStatusEnum.ActiveTimeOffPromotion: {
        return {
          tag: getDiscountTagLabel(true) && (
            <Chip
              label={getDiscountTagLabel(true)}
              size="small"
              sx={{
                borderRadius: 0.5,
                maxWidth: "fit-content",
              }}
              color="info"
              icon={<Tag />}
            />
          ),
          items: [
            {
              label: t`Member since`,
              value: formatDate({ date: created_at, format: "PP" }),
            },
            {
              label: t`Promo period`,
              value: formatPromoPeriod(current_period_start, current_period_end),
            },
          ],
          action: (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.cancel_subscription)}
                variant="text"
                fullWidth
                color="error"
              >
                {t`Cancel subscription`}
              </Button>
            </Stack>
          ),
          description: (
            <Typography variant="prose-base" color="text.primary">
              <Trans>
                After the promo period ends your subscription will be cancelled. To keep access to
                all features, go to manage subscription.
              </Trans>
            </Typography>
          ),
        };
      }
      case UISubscriptionStatusEnum.TrialingTimeOffPromotion: {
        return {
          tag: (
            <Chip
              label={t`Free trial`}
              size="small"
              sx={{
                borderRadius: 0.5,
                maxWidth: "fit-content",
              }}
              color="default"
            />
          ),
          items: [
            {
              label: t`Trial started`,
              value: formatDate({ date: userSubscription.trial_start, format: "PP" }),
            },
            {
              label: t`Trial ends`,
              value: formatDate({ date: userSubscription.trial_end, format: "PP" }),
            },
            {
              label: t`Promo period`,
              value: formatPromoPeriod(current_period_start, current_period_end),
            },
          ],
          description: (
            <Typography variant="prose-base" color="text.primary">
              <Trans>
                After the trial and promo periods end your subscription will be cancelled. To keep
                access to all features go to manage subscription.
              </Trans>
            </Typography>
          ),
          action: (
            <Stack spacing={2}>
              <Button
                size="large"
                onClick={() => handleButtonClick(ClickTarget.manage_subscription)}
                variant="contained"
                fullWidth
                color="primary"
              >
                {t`Manage subscription`}
              </Button>
            </Stack>
          ),
        };
      }
    }
  }, [userSubscription, i18n, userSubscriptionState, handleButtonClick, fitnessAppId]);

  return {
    userSubscription,
    hasActiveSub,
    subscriptionError,
    subscriptionIsLoading,
    subscriptionData,
    offeringsLoading,
    offeringsError,
    makePurchase,
    purchaseInProgress,
    nonSubscribedData,
    pkg,
  };
}

export default useMembership;
